import React, { useEffect } from 'react';

import SingleDeviceDisconnection from './components/SingleDeviceDisconnection';
import SendNotificationsBy from './components/SendNotificationsBy';
import { CheckboxesWrapper, StyledWrapper } from './styled';
import { TechnicalSubscriptionDetails } from 'src/routes/GMDashboard/modules/types';

type Props = {
  technicalSubscriptionDetails: {
    SMS: TechnicalSubscriptionDetails;
    EMAIL: TechnicalSubscriptionDetails;
  };
  handleSubscriptionDetails: (technicalSubscriptionDetails: {
    SMS: TechnicalSubscriptionDetails;
    EMAIL: TechnicalSubscriptionDetails;
  }) => void;
  setHasErrors: (isError: boolean) => void;
  isFormEnabled?: boolean;
};

const DisconnectionAlertsSettingsForm = ({
  technicalSubscriptionDetails,
  handleSubscriptionDetails,
  setHasErrors,
  isFormEnabled = true,
}: Props): JSX.Element => {
  const summaryReportEnabled =
    technicalSubscriptionDetails.EMAIL.summaryReportEnabled ||
    technicalSubscriptionDetails.SMS.summaryReportEnabled;
  const summaryReportTime =
    technicalSubscriptionDetails.EMAIL.summaryReportTime ||
    technicalSubscriptionDetails.SMS.summaryReportTime;
  const singleDisconnectionAllowedType =
    technicalSubscriptionDetails.EMAIL.singleDisconnectionAllowedType ||
    technicalSubscriptionDetails.SMS.singleDisconnectionAllowedType;
  const emailCcEnabled =
    technicalSubscriptionDetails.EMAIL.emailCcEnabled ||
    technicalSubscriptionDetails.SMS.emailCcEnabled;
  const emailCcRecipients =
    technicalSubscriptionDetails.EMAIL.emailCcRecipients ||
    technicalSubscriptionDetails.SMS.emailCcRecipients;
  const isSingleDisconnectionAllowedTypePossible =
    technicalSubscriptionDetails.EMAIL.isAssigned ||
    technicalSubscriptionDetails.SMS.isAssigned;
  const isEmailCcPossible = technicalSubscriptionDetails.EMAIL.isAssigned;

  useEffect(() => {
    setHasErrors(summaryReportEnabled && !summaryReportTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryReportEnabled, summaryReportTime]);

  const handleCommonTechnicalSettingsChanges = (
    summaryReportSettings: Partial<
      Pick<
        TechnicalSubscriptionDetails,
        | 'summaryReportEnabled'
        | 'summaryReportTime'
        | 'singleDisconnectionAllowedType'
        | 'emailCcEnabled'
        | 'emailCcRecipients'
      >
    >,
  ) =>
    handleSubscriptionDetails({
      EMAIL: {
        ...technicalSubscriptionDetails.EMAIL,
        ...summaryReportSettings,
      },
      SMS: {
        ...technicalSubscriptionDetails.SMS,
        ...summaryReportSettings,
      },
    });

  return (
    <StyledWrapper>
      <CheckboxesWrapper>
        <SingleDeviceDisconnection
          settings={{
            isEnabled: !!singleDisconnectionAllowedType,
            deviceDisconnectionType:
              singleDisconnectionAllowedType || 'ALL_DISCONNECTIONS',
          }}
          disabled={!isSingleDisconnectionAllowedTypePossible || !isFormEnabled}
          handleChangeEnabled={isEnabled =>
            handleCommonTechnicalSettingsChanges({
              singleDisconnectionAllowedType: isEnabled
                ? singleDisconnectionAllowedType || 'ALL_DISCONNECTIONS'
                : null,
            })
          }
          handleChangeType={type =>
            handleCommonTechnicalSettingsChanges({
              singleDisconnectionAllowedType: type,
            })
          }
        />
        <SendNotificationsBy
          settings={{
            byEmail: technicalSubscriptionDetails.EMAIL.isAssigned,
            bySMS: technicalSubscriptionDetails.SMS.isAssigned,
            extraEmails: {
              isEnabled: emailCcEnabled,
              emails: emailCcRecipients ? emailCcRecipients.split(',') : [],
            },
          }}
          disabled={!isEmailCcPossible}
          isFormEnabled={isFormEnabled}
          handleChangeSettings={settings =>
            handleSubscriptionDetails({
              EMAIL: {
                ...technicalSubscriptionDetails.EMAIL,
                isAssigned: settings.byEmail,
                emailCcEnabled: settings.extraEmails.isEnabled,
                emailCcRecipients: settings.extraEmails.emails.join(','),
              },
              SMS: {
                ...technicalSubscriptionDetails.SMS,
                isAssigned: settings.bySMS,
                emailCcEnabled: settings.extraEmails.isEnabled,
                emailCcRecipients: settings.extraEmails.emails.join(','),
              },
            })
          }
        />
      </CheckboxesWrapper>
    </StyledWrapper>
  );
};

export default DisconnectionAlertsSettingsForm;
